(function () {
  'use strict';

  function FetchError(object) {
    this.status = object.status || null;
    this.headers = object.headers || null;
    this.json = object.json || null;
    this.body = object.body || null;
  }
  FetchError.prototype = Error.prototype;

  const wrap = (toWrap, wrapperClass = '', wrapperOption) => {
    const wrapper = wrapperOption || document.createElement('div');
    wrapper.classList.add(wrapperClass);
    toWrap.parentNode.insertBefore(wrapper, toWrap);
    return wrapper.appendChild(toWrap);
  };

  function wrapElements(container) {
    const tableSelectors = '.rte table';
    const tables = container.querySelectorAll(tableSelectors);
    tables.forEach((table) => {
      wrap(table, 'rte__table-wrapper');
      table.setAttribute('data-scroll-lock-scrollable', '');
    });

    const iframeSelectors = '.rte iframe[src*="youtube.com/embed"], .rte iframe[src*="player.vimeo"], .rte iframe#admin_bar_iframe';
    const frames = container.querySelectorAll(iframeSelectors);
    frames.forEach((frame) => {
      wrap(frame, 'rte__video-wrapper');
    });
  }

  const classes = {
    added: 'is-added',
    animated: 'is-animated',
    disabled: 'is-disabled',
    error: 'has-error',
    loading: 'is-loading',
    open: 'is-open',
    visible: 'is-visible',
    siblingLinkCurrent: 'sibling__link--current',
  };

  const settings = {
    errorDelay: 3000,
    closeFallbackMs: 600,
  };

  const selectors = {
    animation: '[data-animation]',
    apiContent: '[data-api-content]',
    buttonQuickAdd: '[data-quick-add-btn]',
    buttonAddToCart: '[data-add-to-cart]',
    cartDrawer: 'cart-drawer',
    cartPage: '[data-cart-page]',
    cartLineItems: '[data-line-items]',
    dialog: 'dialog',
    focusable: 'button, [href], select, textarea, [tabindex]:not([tabindex="-1"])',
    messageError: '[data-message-error]',
    modalButton: '[data-quick-add-modal-handle]',
    modalContainer: '[data-product-upsell-container]',
    modalContent: '[data-product-upsell-ajax]',
    modalClose: '[data-quick-add-modal-close]',
    productGridItem: 'data-grid-item',
    quickAddHolder: '[data-quick-add-holder]',
    quickAddModal: '[data-quick-add-modal]',
    quickAddModalTemplate: '[data-quick-add-modal-template]',
  };

  const attributes = {
    closing: 'closing',
    productId: 'data-product-id',
    modalHandle: 'data-quick-add-modal-handle',
    siblingSwapper: 'data-sibling-swapper',
    quickAddHolder: 'data-quick-add-holder',
    bundleProductButton: 'data-bundle-product-button',
  };

  class QuickAddProduct extends HTMLElement {
    static activeModal = null;
    static sourceProductItem = null;
    static fetchController = null;
    static swapGeneration = 0;

    static resetProductCard(productItem) {
      if (!productItem) return;

      if (typeof window.repairProductCardInfo === 'function') {
        window.repairProductCardInfo(productItem);
        return;
      }

      const productInfo = productItem.querySelector('.product-item__info');

      if (productInfo) {
        const template = productInfo.querySelector('template[data-product-card-default]');
        const defaultHtml = template?.innerHTML?.trim() || productItem.dataset.defaultInfoHtml;
        const needsRestore = !productInfo.querySelector('.product-item__title-text, .product-item__title')?.textContent?.trim();

        if (needsRestore && defaultHtml && template) {
          productInfo.querySelectorAll(':scope > :not(template[data-product-card-default])').forEach((node) => {
            node.remove();
          });

          if (template) {
            productInfo.insertBefore(document.createRange().createContextualFragment(defaultHtml), template);
          }
        }

        productInfo.removeAttribute('style');
        productInfo.classList.remove('product-item__info--force-visible');
      }

      productItem.querySelectorAll('[data-quick-add-holder]').forEach((holder) => {
        holder.classList.remove(classes.disabled);
      });
    }

    static resetSourceProductCard() {
      const source = QuickAddProduct.sourceProductItem;

      if (source?.isConnected) {
        QuickAddProduct.resetProductCard(source);
      }

      QuickAddProduct.sourceProductItem = null;
    }

    static syncSourceProductItemAfterSwap(productItem, previousElement) {
      if (!productItem || !previousElement) return;

      if (QuickAddProduct.sourceProductItem === previousElement) {
        QuickAddProduct.sourceProductItem = productItem;
        QuickAddProduct.cacheSourceProductDefaults();
      }
    }

    static cacheSourceProductDefaults() {
      if (!QuickAddProduct.sourceProductItem) return;

      const template = QuickAddProduct.sourceProductItem.querySelector('template[data-product-card-default]');
      const defaultHtml = template?.innerHTML?.trim();

      if (defaultHtml) {
        QuickAddProduct.sourceProductItem.dataset.defaultInfoHtml = defaultHtml;
      }
    }

    static forceCloseModal(modal) {
      if (!modal) return;

      modal.removeAttribute(attributes.closing);
      modal.classList.remove(classes.loading);
      modal.setAttribute('inert', '');

      if (typeof modal.close === 'function') {
        try {
          modal.close();
        } catch (error) {
          modal.removeAttribute('open');
        }
      } else {
        modal.removeAttribute('open');
      }

      if (QuickAddProduct.activeModal === modal) {
        QuickAddProduct.activeModal = null;
      }
    }

    constructor() {
      super();

      this.quickAddHolder = this.querySelector(selectors.quickAddHolder);
      this.modal = null;
      this.currentModal = null;
      this.productId = this.quickAddHolder.getAttribute(attributes.quickAddHolder);
      this.modalButton = this.quickAddHolder.querySelector(selectors.modalButton);
      this.handle = this.modalButton?.getAttribute(attributes.modalHandle);
      this.buttonQuickAdd = this.quickAddHolder.querySelector(selectors.buttonQuickAdd);
      this.buttonATC = this.quickAddHolder.querySelector(selectors.buttonAddToCart);
      this.button = this.modalButton || this.buttonATC;
      this.a11y = window.theme.a11y;
      this.isAnimating = false;
      this.closeTimer = null;
      this.swapGeneration = 0;

      this.modalClose = this.modalClose.bind(this);
      this.modalCloseOnProductAdded = this.modalCloseOnProductAdded.bind(this);
      this.modalButtonClickEvent = this.modalButtonClickEvent.bind(this);
      this.quickAddLoadingToggle = this.quickAddLoadingToggle.bind(this);
    }

    connectedCallback() {
      if (this.modalButton) {
        this.modalButton.addEventListener('click', this.modalButtonClickEvent);
      }

      if (this.buttonATC) {
        this.buttonATC.addEventListener('click', (e) => {
          e.preventDefault();

          window.theme.a11y.lastElement = this.buttonATC;

          document.dispatchEvent(
            new CustomEvent('theme:cart:add', {
              detail: {
                button: this.buttonATC,
              },
            })
          );
        });
      }

      if (this.quickAddHolder) {
        this.quickAddHolder.addEventListener('animationend', this.quickAddLoadingToggle);
        this.errorHandler();
      }
    }

    modalButtonClickEvent(e) {
      e.preventDefault();

      const isSiblingSwapper = this.modalButton.hasAttribute(attributes.siblingSwapper);
      const isSiblingLinkCurrent = this.modalButton.classList.contains(classes.siblingLinkCurrent);

      if (isSiblingLinkCurrent) return;

      this.modalButton.classList.add(classes.loading);
      this.modalButton.disabled = true;

      if (isSiblingSwapper) {
        this.currentModal = e.target.closest(selectors.quickAddModal) || QuickAddProduct.activeModal;

        if (this.currentModal) {
          this.currentModal.classList.add(classes.loading);
        }
      } else {
        QuickAddProduct.sourceProductItem = this.quickAddHolder.closest('product-item');
        QuickAddProduct.cacheSourceProductDefaults();
      }

      this.renderModal();
    }

    modalCreate(response) {
      const cachedModal = document.querySelector(`${selectors.quickAddModal}[${attributes.productId}="${this.productId}"]`);

      if (cachedModal) {
        this.modal = cachedModal;
        this.modalOpen();
      } else {
        const modalTemplate = this.quickAddHolder.querySelector(selectors.quickAddModalTemplate);
        if (!modalTemplate) return;

        const htmlObject = document.createElement('div');
        htmlObject.innerHTML = modalTemplate.innerHTML;

        document.body.appendChild(htmlObject.querySelector(selectors.quickAddModal));
        modalTemplate.remove();

        this.modal = document.querySelector(`${selectors.quickAddModal}[${attributes.productId}="${this.productId}"]`);
        this.modal.querySelector(selectors.modalContent).innerHTML = new DOMParser().parseFromString(response, 'text/html').querySelector(selectors.apiContent).innerHTML;

        this.modalCreatedCallback();
      }
    }

    modalOpen() {
      if (this.currentModal && this.currentModal !== this.modal) {
        QuickAddProduct.forceCloseModal(this.currentModal);
        this.preserveQuickAddTitle(this.modal, this.currentModal);
        this.currentModal = null;
      } else {
        this.preserveQuickAddTitle(this.modal);
      }

      this.isAnimating = false;
      this.clearCloseTimer();

      if (typeof this.modal.show === 'function') {
        this.modal.show();
      }

      this.modal.setAttribute('open', true);
      this.modal.removeAttribute('inert');
      this.modal.classList.remove(classes.loading);
      delete this.modal.dataset.closeComplete;

      QuickAddProduct.activeModal = this.modal;

      if (QuickAddProduct.sourceProductItem) {
        QuickAddProduct.sourceProductItem.querySelectorAll('[data-quick-add-holder]').forEach((holder) => {
          holder.classList.add(classes.disabled);
        });
      }

      if (this.modalButton) {
        this.modalButton.classList.remove(classes.loading);
        this.modalButton.disabled = false;
        window.theme.a11y.lastElement = this.modalButton;
      }

      requestAnimationFrame(() => {
        this.modal.querySelectorAll(selectors.animation).forEach((item) => {
          item.classList.add(classes.animated);
        });
      });

      document.dispatchEvent(new CustomEvent('theme:quick-add:open', {bubbles: true}));
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true}));
      document.addEventListener('theme:product:added', this.modalCloseOnProductAdded, {once: true});
      document.addEventListener('theme:bundle:added', this.modalCloseOnProductAdded, {once: true});
    }

    clearCloseTimer() {
      if (this.closeTimer) {
        clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    finishModalClose() {
      if (!this.modal || this.modal.dataset.closeComplete === 'true') return;

      this.modal.dataset.closeComplete = 'true';
      this.clearCloseTimer();
      this.isAnimating = false;

      if (typeof this.modal.close === 'function') {
        try {
          this.modal.close();
        } catch (error) {
          this.modal.removeAttribute('open');
        }
      } else {
        this.modal.removeAttribute('open');
      }

      this.modal.removeAttribute(attributes.closing);
      this.modal.setAttribute('inert', '');
      this.modal.classList.remove(classes.loading);

      if (QuickAddProduct.activeModal === this.modal) {
        QuickAddProduct.activeModal = null;
      }

      if (this.modalButton && !this.modalButton.hasAttribute(attributes.bundleProductButton)) {
        this.modalButton.disabled = false;
      }

      this.resetAnimatedItems();

      if (!window.theme.hasOpenModals()) {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
        QuickAddProduct.resetSourceProductCard();
        QuickAddProduct.cacheSourceProductDefaults();
      }

      document.removeEventListener('theme:product:added', this.modalCloseOnProductAdded);
      document.removeEventListener('theme:bundle:added', this.modalCloseOnProductAdded);

      this.a11y.removeTrapFocus();
      this.a11y.autoFocusLastElement();
    }

    modalClose() {
      if (!this.modal) return;

      if (QuickAddProduct.activeModal && QuickAddProduct.activeModal !== this.modal) {
        return;
      }

      if (this.isAnimating) {
        return;
      }

      if (!this.modal.hasAttribute(attributes.closing)) {
        this.modal.setAttribute(attributes.closing, '');
        this.isAnimating = true;

        this.closeTimer = setTimeout(() => {
          this.finishModalClose();
        }, settings.closeFallbackMs);

        return;
      }

      this.finishModalClose();
    }

    modalEvents() {
      this.modal.querySelector(selectors.modalClose)?.addEventListener('click', (e) => {
        e.preventDefault();
        this.modalClose();
      });

      this.modal.addEventListener('click', (event) => {
        if (event.target.nodeName === 'DIALOG' && event.type === 'click') {
          this.modalClose();
        }
      });

      this.modal.addEventListener('keydown', (event) => {
        if (event.code == 'Escape') {
          event.preventDefault();
          this.modalClose();
        }
      });

      this.modal.addEventListener('theme:modal:close', () => {
        QuickAddProduct.forceCloseModal(this.modal);
        this.finishModalClose();
      });

      this.modal.addEventListener('animationend', (event) => {
        if (event.target !== this.modal) return;

        if (this.modal.hasAttribute(attributes.closing)) {
          this.finishModalClose();
        } else {
          setTimeout(() => {
            this.a11y.trapFocus(this.modal);
            const focusTarget = this.modal.querySelector('[autofocus]') || this.modal.querySelector(selectors.focusable);
            focusTarget?.focus();
          }, 50);
        }
      });
    }

    modalCloseOnProductAdded() {
      this.resetQuickAddButtons();
      if (this.modal && this.modal.hasAttribute('open')) {
        this.modalClose();
      }
    }

    quickAddLoadingToggle(e) {
      if (e.target != this.quickAddHolder) return;

      this.quickAddHolder.classList.remove(classes.disabled);
    }

    errorHandler() {
      this.quickAddHolder.addEventListener('theme:cart:error', (event) => {
        const holder = event.detail.holder;
        const parentProduct = holder.closest(`[${selectors.productGridItem}]`);
        if (!parentProduct) return;

        const errorMessageHolder = holder.querySelector(selectors.messageError);
        const button = holder.querySelector(selectors.buttonAddToCart);

        if (button) {
          button.classList.remove(classes.added, classes.loading);
          holder.classList.add(classes.error);
        }

        if (errorMessageHolder) {
          errorMessageHolder.innerText = event.detail.description;
        }

        setTimeout(() => {
          this.resetQuickAddButtons();
        }, settings.errorDelay);
      });
    }

    resetQuickAddButtons() {
      if (this.quickAddHolder) {
        this.quickAddHolder.classList.remove(classes.visible, classes.error);
      }

      if (this.buttonQuickAdd && !this.buttonQuickAdd.hasAttribute(attributes.bundleProductButton)) {
        this.buttonQuickAdd.classList.remove(classes.added);
        this.buttonQuickAdd.disabled = false;
      }
    }

    renderModal() {
      const cachedModal = document.querySelector(`${selectors.quickAddModal}[${attributes.productId}="${this.productId}"]`);

      if (cachedModal) {
        QuickAddProduct.swapGeneration += 1;
        this.modal = cachedModal;
        this.modalOpen();
        return;
      }

      QuickAddProduct.fetchController?.abort();
      QuickAddProduct.fetchController = new AbortController();

      const swapGeneration = ++QuickAddProduct.swapGeneration;
      this.swapGeneration = swapGeneration;

      const apiUrl = this.modalButton.hasAttribute(attributes.bundleProductButton) ? 'api-product-bundle' : 'api-product-upsell';

      window
        .fetch(`${window.theme.routes.root}products/${this.handle}?section_id=${apiUrl}`, {
          signal: QuickAddProduct.fetchController.signal,
        })
        .then(this.upsellErrorsHandler)
        .then((response) => response.text())
        .then((response) => {
          if (swapGeneration !== QuickAddProduct.swapGeneration) return;

          this.modalCreate(response);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;

          console.error(error);

          if (this.modalButton) {
            this.modalButton.classList.remove(classes.loading);
            this.modalButton.disabled = false;
          }

          if (this.currentModal) {
            this.currentModal.classList.remove(classes.loading);
          }
        });
    }

    modalCreatedCallback() {
      this.modalEvents();
      this.modalOpen();
      wrapElements(this.modal);
    }

    upsellErrorsHandler(response) {
      if (!response.ok) {
        return response.json().then(function (json) {
          const e = new FetchError({
            status: response.statusText,
            headers: response.headers,
            json: json,
          });
          throw e;
        });
      }
      return response;
    }

    resetAnimatedItems() {
      this.modal?.querySelectorAll(selectors.animation).forEach((item) => {
        item.classList.remove(classes.animated);
      });
    }

    preserveQuickAddTitle(modal, previousModal = null) {
      if (!modal) return;

      const titleEl = modal.querySelector('[data-quick-add-title]');
      if (!titleEl) return;

      const savedTitle = previousModal?.dataset?.quickAddTitle || modal.dataset.quickAddTitle;

      if (savedTitle) {
        titleEl.textContent = savedTitle;
        modal.dataset.quickAddTitle = savedTitle;
        return;
      }

      modal.dataset.quickAddTitle = titleEl.textContent.trim();
    }
  }

  if (!customElements.get('quick-add-product')) {
    customElements.define('quick-add-product', QuickAddProduct);
  }

  document.addEventListener('theme:scroll:unlock', () => {
    QuickAddProduct.resetSourceProductCard();
  });

  document.addEventListener('theme:html:change', (event) => {
    const swapped = event.detail?.element;
    const productItem = swapped?.matches?.('product-item') ? swapped : swapped?.closest?.('product-item');

    QuickAddProduct.syncSourceProductItemAfterSwap(productItem, event.detail?.previousElement);
  });

})();
